'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useHydration } from '@/hooks/useHydration';
import { MessageLoader } from '@/components/ui/chat/MessageLoader';

function AuthGuardFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--color-background))]">
      <MessageLoader />
    </div>
  );
}

function AuthGuardInner({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated: authAuthenticated, isLoading: authLoading } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);

  const searchParams = useSearchParams();
  const hasOauthTx = Boolean(searchParams.get('oauth_tx'));

  const isOauthLoginContinuation = pathname === '/login' && hasOauthTx;
  const isOauthCallbackContinuation = pathname === '/auth/callback' && hasOauthTx;

  useHydration();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (authLoading || !isHydrated) return;

    const isFullyAuthenticated = Boolean(authAuthenticated);

    if (isFullyAuthenticated && pathname === '/login' && !isOauthLoginContinuation) {
      router.replace('/');
      return;
    }

    if (!isFullyAuthenticated && pathname !== '/login' && !isOauthCallbackContinuation) {
      router.replace('/login');
    }
  }, [authAuthenticated, pathname, router, authLoading, isHydrated, isOauthLoginContinuation, isOauthCallbackContinuation]);

  if (authLoading || !isHydrated) {
    return <AuthGuardFallback />;
  }

  const isFullyAuthenticated = Boolean(authAuthenticated);

  if (isFullyAuthenticated && pathname === '/login' && !isOauthLoginContinuation) {
    return <AuthGuardFallback />;
  }

  if (!isFullyAuthenticated && pathname !== '/login' && !isOauthCallbackContinuation) {
    return <AuthGuardFallback />;
  }

  return <>{children}</>;
}

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<AuthGuardFallback />}>
      <AuthGuardInner>{children}</AuthGuardInner>
    </Suspense>
  );
}
